import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Loader from '../../../components/loader'
import Button from '../../../elements/button'
import { Link, Tooltip } from 'react-daisyui'
import Icon from '../../../components/icons'
import NumericStepper from '../../../components/NumericStepper'
import Dropdown from '../../../elements/Form/dropdown'
import { useFieldArray, useForm } from 'react-hook-form'
import { showTrendAs } from '../../../_config'
import { useExperimentSelector } from '../../../store/selectors/experimentSelector'
import { getSelectedOption, trackEvent } from '../../../_common/functions'
import { useExperimentApi } from '../../../store/api/hooks/experimentApiHook'
import toast from 'react-hot-toast'
import { useFpLoader } from '../../../store/api/hooks/fpLoaderHook'
import { useMeasurementConfigure } from '../../../_common/hook/useMeasurementConfigure'
interface CreateConfigMatchingParametersProps {
    _ref: React.RefObject<HTMLDialogElement>
    type: any
    matchingParameters: {
        TREAT_OUTLIER: {
            tooltip: string
            value: boolean
            type: string
            label: string
        }
        OUTLIER_THRESHOLD: {
            tooltip: string
            value: number
            type: string
            label: string
        }
        ROLLING_MODE: {
            tooltip: string
            value: boolean
            type: string
            label: string
        }
    }
    setMatchingParameters: React.Dispatch<
        React.SetStateAction<{
            TREAT_OUTLIER: {
                tooltip: string
                value: boolean
                type: string
                label: string
            }
            OUTLIER_THRESHOLD: {
                tooltip: string
                value: number
                type: string
                label: string
            }
            ROLLING_MODE: {
                tooltip: string
                value: boolean
                type: string
                label: string
            }
        }>
    >
    handleContinue: () => void
    disabled: boolean
}

const ToggleSwitch: React.FC<{
    label: string
    tooltip: string
    enabled: boolean
    onToggle: () => void
}> = ({ label, tooltip, enabled, onToggle }) => {
    const { t } = useTranslation()

    return (
        <div className="flex justify-between items-center w-full">
            <span className=" items-center flex font-['Poppins'] font-normal text-[14px] leading-[100%] tracking-[0px]">
                {label} &nbsp;
                <Tooltip message={t(tooltip)} color="info" position={'top'}>
                    <Icon name="outlineInfoCircle" size={'0.60'} />
                </Tooltip>
            </span>

            <div className="flex items-center gap-2">
                <span className="font-['Poppins'] font-normal text-[14px] text-[#464E5F]">{t('False')}</span>
                <button type="button" className={`w-[56px] h-[24px] flex items-center rounded-full px-[2px] cursor-pointer ${enabled ? 'bg-[#343A40]' : 'bg-[#76829A]'}`} onClick={onToggle}>
                    <div className={`w-[20px] h-[20px] rounded-full shadow-md transform duration-300 ease-in-out ${enabled ? 'translate-x-[32px] bg-primary' : 'translate-x-0 bg-black'}`}></div>
                </button>
                <span className="font-['Poppins'] font-normal text-[14px] text-[#464E5F]">{t('True')}</span>
            </div>
        </div>
    )
}

const CreateConfigMatchingParameters: React.FC<CreateConfigMatchingParametersProps> = ({ _ref, type, matchingParameters, setMatchingParameters, handleContinue, disabled }) => {
    const { t } = useTranslation()
    const measurementHookFunction = useMeasurementConfigure({ experimentType: type })
    const experimentData = useExperimentSelector()
    const experimentAPI: any = useExperimentApi()
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const { showLoader, hideLoader } = useFpLoader()
    const { control } = useForm<any>()
    const {
        fields: kpis,
        append: kpisAppend,
        update: kpisUpdate,
        remove: kpisRemove,
    } = useFieldArray({
        control,
        name: 'kpis',
    })
    useEffect(() => {
        trackEvent('wizard_matching_params_modal_opened', { experiment_literal: experimentData?.experiment_literal })
    }, [])

    const handleClose = () => {
        trackEvent('wizard_matching_params_modal_closed', { experiment_literal: experimentData?.experiment_literal })
        if (_ref.current && typeof _ref.current.close === 'function') {
            _ref.current.close()
        }
    }
    const submitKpiDirection = () => {
        showLoader()
        const payload: any = {}
        payload.kpis = kpis
            .filter((kpi: any) => {
                return kpi.is_custom
            })
            .map((kpi: any) => {
                return {
                    literral_value: kpi.literral_value,
                    kpi_direction: kpi.kpi_direction?.value,
                }
            })
        payload.literal = experimentData.experiment_literal
        console.log(payload)
        experimentAPI.updateKpiDirection(
            payload,
            (resp: any) => {
                hideLoader()
                setIsOpen(false)
                console.log(resp)
            },
            (err: any) => {
                hideLoader()
                toast.error(err?.message)
            },
        )
    }
    useEffect(() => {
        const metrices = experimentData?.experiment_metadatas?.metrices

        if (!metrices) return

        const updatedKpis = metrices.map((kpi: any) => ({
            ...kpi,
            kpi_direction: getSelectedOption(kpi?.kpi_direction?.value || kpi.trends_as || 'positive_upword', showTrendAs),
        }))

        kpisRemove()
        kpisAppend(updatedKpis)
    }, [experimentData?.experiment_metadatas?.metrices, showTrendAs])

    return (
        <dialog ref={_ref} className="modal">
            <div className="bg-white w-[571px] top-[140px] rounded-[20px] px-[26px] py-[25px] flex flex-col gap-[25px] font-['Poppins'] shadow-md">
                <h2 className="text-[24px] font-semibold leading-[100%] text-center">{t('Measurement Parameters')}</h2>

                <div className="w-[509px] border border-[#0000001A]" />

                <div className="flex gap-4 justify-between items-center">
                    <div className="flex justify-between items-center">
                        <span className="font-['Poppins'] font-normal text-[14px] leading-[100%] tracking-[0px]">
                            {t('Change the KPI Trend Direction')} &nbsp;
                            <Tooltip
                                message={t(
                                    'Select whether an increase or decrease indicates success for this KPI. For eg: Increase in Net Revenue indicates success (positive upward trend), while decrease in Delivery Time indicates success (positive downward trend).',
                                )}
                                color="info"
                                position={'top'}
                            >
                                <Icon name="outlineInfoCircle" size={'0.60'} />
                            </Tooltip>
                        </span>
                    </div>
                    <div className="flex items-center">
                        <Icon name="edit" size={'0.60'} onClick={() => setIsOpen(!isOpen)} />
                    </div>
                </div>
                <div className={`flex flex-col gap-4 transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0 overflow-hidden'}`}>
                    {kpis.map((item: any, index: number) => (
                        <div key={'kpi' + index} className="flex  justify-between items-center">
                            <div className="flex justify-between items-center font-['Poppins'] font-normal text-[0.583rem]">{item.literral_value}</div>
                            {item.is_custom === true ? (
                                <div className="flex items-center w-[9.583rem]">
                                    <Dropdown
                                        className="w-full"
                                        required={true}
                                        value={getSelectedOption(item?.kpi_direction?.value, showTrendAs)}
                                        onChange={(value: any) => {
                                            kpisUpdate(index, { ...item, kpi_direction: value })
                                        }}
                                        options={showTrendAs}
                                    />
                                </div>
                            ) : (
                                <div className="flex items-center">--</div>
                            )}
                        </div>
                    ))}

                    <div className="flex items-center justify-center gap-2">
                        <button type="button" onClick={() => setIsOpen(false)} className="w-[5.292rem] h-[1.333rem] rounded-[2.083rem] bg-[#fff] flex items-center justify-center px-4 py-1 text-[0.583rem] border border-[#74788D]" title={t('Cancel')}>
                            {t('Cancel')}
                        </button>

                        <button type="button" onClick={submitKpiDirection} className="w-[5.292rem] h-[1.333rem] rounded-[2.083rem] bg-primary flex items-center justify-center text-[0.583rem] px-4 py-1" title={t('Proceed')}>
                            {t('Proceed')}
                        </button>
                    </div>
                </div>

                <div className="flex flex-col gap-4">
                    {Object.entries(matchingParameters).map(([key, prop]) => {
                        if (prop.type === 'switch') {
                            return (
                                <ToggleSwitch
                                    key={key}
                                    label={prop.label}
                                    tooltip={prop.tooltip}
                                    enabled={prop.value as boolean}
                                    onToggle={() => {
                                        trackEvent('wizard_matching_param_toggled', { experiment_literal: experimentData?.experiment_literal, param_name: key, value: !matchingParameters[key as keyof typeof matchingParameters].value })
                                        if (key === 'TREAT_OUTLIER')
                                            trackEvent('measurement_config_treat_outlier_toggled', { experiment_literal: experimentData?.experiment_literal, enabled: !matchingParameters[key as keyof typeof matchingParameters].value })
                                        if (key === 'ROLLING_MODE')
                                            trackEvent('measurement_config_rolling_mode_toggled', { experiment_literal: experimentData?.experiment_literal, enabled: !matchingParameters[key as keyof typeof matchingParameters].value })
                                        if (key === 'DYNA_CLUSTERING')
                                            trackEvent('measurement_config_dyna_clustering_toggled', { experiment_literal: experimentData?.experiment_literal, enabled: !matchingParameters[key as keyof typeof matchingParameters].value })
                                        if (key === 'GENERALIZED_SC')
                                            trackEvent('measurement_config_generalized_sc_toggled', { experiment_literal: experimentData?.experiment_literal, enabled: !matchingParameters[key as keyof typeof matchingParameters].value })
                                        if (key === 'PANEL_GSC')
                                            trackEvent('measurement_config_panel_gsc_toggled', { experiment_literal: experimentData?.experiment_literal, enabled: !matchingParameters[key as keyof typeof matchingParameters].value })
                                        setMatchingParameters((prev) => ({
                                            ...prev,
                                            [key]: {
                                                ...prev[key as keyof typeof matchingParameters],
                                                value: !prev[key as keyof typeof matchingParameters].value,
                                            },
                                        }))
                                    }}
                                />
                            )
                        } else if (prop.type === 'numericStepper') {
                            return (
                                <>
                                    {key == 'OUTLIER_THRESHOLD' && matchingParameters.TREAT_OUTLIER.value && (
                                        <div key={key} className="flex justify-between items-center">
                                            <span className="font-['Poppins'] font-normal text-[14px] leading-[100%] tracking-[0px]">
                                                {prop.label} &nbsp;
                                                <Tooltip message={prop.tooltip} color="info" position={'top'}>
                                                    <Icon name="outlineInfoCircle" size={'0.60'} />
                                                </Tooltip>
                                            </span>
                                            <NumericStepper
                                                min={0.001}
                                                max={0.5}
                                                step={0.001}
                                                defaultValue={prop.value as number}
                                                onChange={(value) => {
                                                    console.log('OUTLIER_THRESHOLD value:', value)
                                                    trackEvent('measurement_config_outlier_threshold_changed', { experiment_literal: experimentData?.experiment_literal, value })
                                                    setMatchingParameters((prev) => ({
                                                        ...prev,
                                                        [key]: {
                                                            ...prev[key as keyof typeof matchingParameters],
                                                            value: Number(Number(value).toFixed(3)),
                                                        },
                                                    }))
                                                }}
                                            />
                                        </div>
                                    )}
                                </>
                            )
                        }
                        return null
                    })}
                </div>

                <div className="my-3">
                    <p className="text-[0.55rem] text-[#222222] leading-[1.3]">
                        {t(`We've simplified this for you: Panel GSC, Generalised SC, and Dyna Clustering are now handled automatically by TestOps based on your experiment data — removing guesswork and inconsistent tuning. Curious about the logic?`)}
                        <br />
                        <Link
                            className="text-blue-600"
                            target="_blank"
                            rel="noreferrer"
                            href="https://testops-docs.ab-inbev.com/SDK%20User%20Guide/3.%20Measurement/3.1.%20Continuous%20KPI/3.1.1.%20Panel%20DID%20&%20Synthetic%20Control/config_internalization/"
                        >
                            {t('Read the methodology')}
                        </Link>
                    </p>
                </div>

                <div className="flex justify-end gap-4 mt-auto">
                    <button type="button" onClick={handleClose} className="w-[143px] h-[50px] px-[25px] py-[10px] border border-gray-400 rounded-[10px] text-black text-[20px] font-medium leading-[100%]">
                        {t('Close')}
                    </button>

                    <Button
                        className="w-[143px] h-[50px] px-[25px] py-[10px] rounded-[10px] bg-black text-white text-[20px] font-medium leading-[100%]"
                        theme="secondary"
                        size="sm"
                        onClick={() => {
                            measurementHookFunction.checkBeforeMeasurementStart(null, handleContinue)
                        }}
                        disabled={disabled}
                    >
                        {disabled ? <Loader /> : t('Continue')}
                    </Button>
                </div>
            </div>
        </dialog>
    )
}

export default CreateConfigMatchingParameters
